import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { X, Plus, Trash2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { TIME_SLOTS_CONFIG } from "@/lib/constants";

interface TimeSlot {
  startTime: string;
  endTime: string;
}

interface TimeBlockModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (slots: {startTime: string, endTime: string}[]) => void;
  editingBlock?: {
    id: number;
    startTime: string;
    endTime: string;
  } | null;
}

export function TimeBlockModal({ isOpen, onClose, onSave, editingBlock }: TimeBlockModalProps) {
  const { toast } = useToast();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([
    { startTime: "11:00", endTime: "12:00" }
  ]);

  // Fetch blocked dates to disable them in calendar
  const { data: blockedDates = [] } = useQuery({
    queryKey: ["blocked-dates"],
    queryFn: async () => {
      const res = await fetch("/api/admin/blocked-dates");
      if (!res.ok) throw new Error("Failed to fetch blocked dates");
      return res.json();
    },
  });

  // Fetch blocked time slots for the selected date
  const { data: fetchedBlockedSlots = [] } = useQuery({
    queryKey: ["blocked-time-slots", selectedDate],
    queryFn: async () => {
      if (!selectedDate) return [];
      const res = await fetch(`/api/admin/blocked-time-slots?date=${format(selectedDate, "yyyy-MM-dd")}`);
      if (!res.ok) throw new Error("Failed to fetch blocked time slots");
      return res.json();
    },
    enabled: !!selectedDate,
  });
  
  const existingBlockedSlots = fetchedBlockedSlots;

  useEffect(() => {
    if (editingBlock) {
      try {
        const startDate = new Date(editingBlock.startTime);
        
        if (isNaN(startDate.getTime())) {
          console.error("Invalid date in editingBlock:", editingBlock.startTime);
          setSelectedDate(undefined);
          setTimeSlots([{ startTime: "11:00", endTime: "12:00" }]);
          return;
        }
        
        setSelectedDate(startDate);
        
        const startHours = startDate.getHours();
        const startMinutes = startDate.getMinutes();
        
        const endDate = new Date(editingBlock.endTime);
        
        const endHours = endDate.getHours();
        const endMinutes = endDate.getMinutes();
        
        setTimeSlots([{
          startTime: `${String(startHours).padStart(2, '0')}:${String(startMinutes).padStart(2, '0')}`,
          endTime: `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`
        }]);
      } catch (error) {
        console.error("Error setting up editing block:", error, editingBlock);
        setSelectedDate(undefined);
        setTimeSlots([{ startTime: "11:00", endTime: "12:00" }]);
      }
    } else {
      setTimeSlots([{ startTime: "11:00", endTime: "12:00" }]);
      setSelectedDate(undefined);
    }
  }, [editingBlock]);

  // Generate available time options
  const generateTimeOptions = () => {
    const options = [];
    const startHour = TIME_SLOTS_CONFIG.START_HOUR;
    const endHour = TIME_SLOTS_CONFIG.END_HOUR;

    for (let hour = startHour; hour <= endHour; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        if (hour === endHour && minute > 0) break;
        const timeStr = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        options.push(timeStr);
      }
    }
    return options;
  };

  const timeOptions = generateTimeOptions();

  // Format time for display
  const formatTimeDisplay = (time: string) => {
    const [hours, minutes] = time.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    return `${displayHours}:${String(minutes).padStart(2, '0')} ${period}`;
  };


  const handleSave = () => {
    if (!selectedDate) {
      toast({
        title: "Error",
        description: "Please select a date",
        variant: "destructive",
      });
      return;
    }

    if (timeSlots.length === 0) {
      toast({
        title: "Error",
        description: "Please add at least one time slot",
        variant: "destructive",
      });
      return;
    }

    // Validate time slots
    for (const slot of timeSlots) {
      if (slot.startTime >= slot.endTime) {
        toast({
          title: "Error",
          description: "End time must be after start time",
          variant: "destructive",
        });
        return;
      }
    }

    // Format slots with date and return to parent
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    const formattedSlots = timeSlots.map(slot => ({
      startTime: `${dateStr}T${slot.startTime}:00`,
      endTime: `${dateStr}T${slot.endTime}:00`,
    }));
    
    onSave(formattedSlots);
    
    toast({
      title: "Time slots added",
      description: `Added ${timeSlots.length} time slot${timeSlots.length !== 1 ? 's' : ''} to be blocked`,
    });
    
    setSelectedDate(undefined);
    setTimeSlots([{ startTime: "11:00", endTime: "12:00" }]);
    onClose();
  };

  const addTimeSlot = () => {
    setTimeSlots([...timeSlots, { startTime: "11:00", endTime: "12:00" }]);
  };

  const removeTimeSlot = (index: number) => {
    setTimeSlots(timeSlots.filter((_, i) => i !== index));
  };

  const updateTimeSlot = (index: number, field: 'startTime' | 'endTime', value: string) => {
    const updated = [...timeSlots];
    updated[index][field] = value;
    setTimeSlots(updated);
  };

  // Check if a date is blocked
  const isDateBlocked = (date: Date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return blockedDates.some((blocked: any) => {
      const blockedDateStr = blocked.date.split('T')[0];
      return blockedDateStr === dateStr;
    });
  };

  // Check if time slot overlaps with existing blocked slots
  const isTimeSlotOverlapping = (slot: TimeSlot) => {
    if (!selectedDate) return false;
    
    return existingBlockedSlots.some((blocked: any) => {
      const blockedStart = blocked.startTime.split('T')[1].substring(0, 5);
      const blockedEnd = blocked.endTime.split('T')[1].substring(0, 5);
      
      // Check for overlap
      return (
        (slot.startTime >= blockedStart && slot.startTime < blockedEnd) ||
        (slot.endTime > blockedStart && slot.endTime <= blockedEnd) ||
        (slot.startTime <= blockedStart && slot.endTime >= blockedEnd)
      );
    });
  };

  // Disable past dates, weekends, and already blocked dates
  const disabledDays = [
    { before: new Date() },
    (date: Date) => {
      const day = date.getDay();
      return day === 0 || day === 6; // Sunday or Saturday
    },
    (date: Date) => isDateBlocked(date),
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#171717] border-white/10 text-white max-w-lg">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl">
              {editingBlock ? "Edit Time Block" : "Select a date and time"}
            </DialogTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="hover:bg-white/10"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-gray-400">
            Select when to block out the calendar.
          </p>

          {/* Date Selection */}
          <div className="space-y-2">
            <Label>Date</Label>
            <div className="flex justify-center">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                disabled={disabledDays}
                className="rounded-md border border-white/10"
                classNames={{
                  months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                  month: "space-y-4",
                  caption: "flex justify-center pt-1 relative items-center",
                  caption_label: "text-sm font-medium",
                  nav: "space-x-1 flex items-center",
                  nav_button: "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
                  nav_button_previous: "absolute left-1",
                  nav_button_next: "absolute right-1",
                  table: "w-full border-collapse space-y-1",
                  head_row: "flex",
                  head_cell: "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
                  row: "flex w-full mt-2",
                  cell: "text-center text-sm p-0 relative [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
                  day: "h-9 w-9 p-0 font-normal aria-selected:opacity-100 hover:bg-white/10 rounded-md",
                  day_selected: "bg-white text-black hover:bg-white hover:text-clubhouse-black focus:bg-white focus:text-black",
                  day_today: "bg-white/10",
                  day_outside: "text-muted-foreground opacity-50",
                  day_disabled: "text-muted-foreground opacity-50 line-through",
                  day_range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
                  day_hidden: "invisible",
                }}
              />
            </div>
          </div>

          {selectedDate && !isNaN(selectedDate.getTime()) && (
            <div className="p-3 bg-black/50 rounded-md">
              <Label className="text-sm text-gray-400">
                {format(selectedDate, "EEEE, MMMM d, yyyy")}
              </Label>
            </div>
          )}

          {/* Time Slots */}
          {selectedDate && !isNaN(selectedDate.getTime()) && (
            <div className="space-y-3">
              {timeSlots.map((slot, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Select
                    value={slot.startTime}
                    onValueChange={(value) => updateTimeSlot(index, 'startTime', value)}
                  >
                    <SelectTrigger className="w-32 bg-black border-white/10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {timeOptions.map((time) => (
                        <SelectItem key={time} value={time}>
                          {formatTimeDisplay(time)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <span className="text-gray-400">-</span>

                  <Select
                    value={slot.endTime}
                    onValueChange={(value) => updateTimeSlot(index, 'endTime', value)}
                  >
                    <SelectTrigger className="w-32 bg-black border-white/10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {timeOptions
                        .filter(time => time > slot.startTime)
                        .map((time) => (
                          <SelectItem key={time} value={time}>
                            {formatTimeDisplay(time)}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>

                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => addTimeSlot()}
                    className="hover:bg-white/10"
                    disabled={editingBlock !== null}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>

                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeTimeSlot(index)}
                    className="hover:bg-white/10 hover:text-red-500"
                    disabled={timeSlots.length === 1}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button
            variant="outline"
            onClick={onClose}
            className="border-white/20 hover:bg-white/10 text-white"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!selectedDate || timeSlots.length === 0}
            className="bg-white hover:bg-white/90 text-black font-medium"
          >
            Add
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}